import { useState, useCallback } from "react";
import { Link } from "react-router";
import { useAuth, useOrganization } from "@clerk/react";
import { Search, Check, RotateCcw, Trash2 } from "lucide-react";
import {
  Button,
  ConfirmDialog,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useAsyncData,
  AsyncState,
  toast,
} from "@zero/ui";
import type { IssueSummary } from "@zero/errors-core";
import * as api from "@/products/errors/lib/api";
import { LevelBadge, StatusBadge } from "@/products/errors/components/badges";

export default function IssuesPage() {
  const { getToken } = useAuth();
  const { organization } = useOrganization();
  const [project, setProject] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<IssueSummary | null>(null);

  const tokenFn = useCallback(() => getToken(), [getToken]);

  const state = useAsyncData(
    () =>
      api.listIssues(
        tokenFn,
        project.trim() || undefined,
        showResolved ? undefined : "open",
      ),
    [tokenFn, project, showResolved, organization?.id],
  );
  const { reload } = state;

  const toggleStatus = async (issue: IssueSummary) => {
    const next = issue.status === "open" ? "resolved" : "open";
    setPendingId(issue.id);
    try {
      await api.setIssueStatus(tokenFn, issue.id, next);
      reload();
    } finally {
      setPendingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    await api.deleteIssue(tokenFn, deleting.id);
    reload();
    toast.success("Issue deleted");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Issues</h1>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Filter by project"
            value={project}
            onChange={(e) => setProject(e.target.value)}
          />
        </div>
        <Button
          variant={showResolved ? "default" : "outline"}
          size="sm"
          onClick={() => setShowResolved((v) => !v)}
        >
          {showResolved ? "Hide resolved" : "Show resolved"}
        </Button>
      </div>

      <AsyncState state={state} onRetry={reload}>
        {({ issues }) =>
          issues.length === 0 ? (
            <p className="text-muted-foreground">
              No issues yet. Send your first error report — see the{" "}
              <a
                href="https://docs.zeroapps.dev/errors/getting-started/"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
              >
                getting-started guide
              </a>
              .
            </p>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead>Last seen</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issues.map((issue) => (
                    <TableRow key={issue.id}>
                      <TableCell className="font-mono text-xs">{issue.project}</TableCell>
                      <TableCell className="max-w-md truncate">
                        <Link to={`/errors/issues/${issue.id}`} className="font-medium hover:underline">
                          {issue.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <LevelBadge level={issue.level} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{issue.count}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(issue.lastSeenAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={issue.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant={issue.status === "open" ? "default" : "outline"}
                            disabled={pendingId === issue.id}
                            onClick={() => void toggleStatus(issue)}
                          >
                            {issue.status === "open" ? (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Resolve
                              </>
                            ) : (
                              <>
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Reopen
                              </>
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={`Delete issue ${issue.title}`}
                            onClick={() => setDeleting(issue)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        }
      </AsyncState>

      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setDeleting(null);
          }}
          title="Delete this issue?"
          description={`"${deleting.title}" and its stored events will be removed from ZeroErrors. This cannot be undone here. If the same error is reported again it comes back as a new issue.`}
          confirmLabel="Delete issue"
          onConfirm={confirmDelete}
          onError={(error) =>
            toast.error(
              error instanceof Error ? error.message : "Could not delete the issue",
            )
          }
        />
      )}
    </div>
  );
}
